/**
 * pdp-afterpay-sync.js
 *
 * Hybrid sync: keep the Afterpay (Square) on-site-messaging app widget exactly
 * as the app renders it — logo, the "(i)" popup, and all the legal copy — and
 * override ONLY the displayed installment amount so it reflects the purchase-
 * matrix selection (quantity tier + one-time vs subscription) instead of the
 * fixed variant catalog price.
 *
 * The widget is a <square-placement> custom element with an OPEN shadow root:
 *
 *   <square-placement … data-platform="shopify-app-blocks">
 *     #shadow-root (open)
 *       <p class="afterpay-paragraph">
 *         <span class="afterpay-main-text">
 *           "or 4 interest-free payments of " <strong>$4.50</strong> " with "
 *         </span>
 *         <span class="afterpay-logo-link"> … logo + (i) modal … </span>
 *       </p>
 *
 * Because the shadow root is OPEN we can reach `.afterpay-main-text strong` and
 * rewrite just the amount to total ÷ 4 (Afterpay is always pay-in-4). The popup
 * is generic ("payment amounts shown are estimates…") so it needs no changes.
 *
 * The app re-renders the widget (resetting the amount to the catalog price), so
 * we DEFEND the value with a MutationObserver on each open shadow root and
 * re-apply. Writes are idempotent (skip if already our value), so the observer
 * can't loop. No-ops entirely if no placement is present.
 */
(() => {
  const PLACEMENTS = 'square-placement, afterpay-placement, clearpay-placement, cash-app-afterpay-placement';
  const AMOUNT_SEL = '.afterpay-main-text strong, .afterpay-paragraph strong';
  const INSTALLMENTS = 4; // Afterpay pay-in-4

  let targetCents = null; // per-installment amount we want shown (cents)
  let moneyFormat;        // cached Shopify money format string

  // Match the store's money format (currency symbol / separators) by reading
  // the format the purchase matrix already ships in its data blob.
  function getMoneyFormat() {
    if (moneyFormat !== undefined) return moneyFormat;
    const el = document.querySelector('[data-role="matrix-data"]');
    try { moneyFormat = el ? (JSON.parse(el.textContent).moneyFormat || '') : ''; }
    catch (e) { moneyFormat = ''; }
    return moneyFormat;
  }

  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    const fmt = getMoneyFormat();
    if (!fmt) return '$' + dollars;
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, dollars)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(cents / 100))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, dollars.replace('.', ','));
  }

  // Every amount node across all placements (each one's OPEN shadow root).
  function amountEls() {
    const els = [];
    document.querySelectorAll(PLACEMENTS).forEach((host) => {
      const root = host.shadowRoot;
      if (root) root.querySelectorAll(AMOUNT_SEL).forEach((el) => els.push(el));
    });
    return els;
  }

  // Write the amount; returns how many nodes were found (0 = not rendered yet).
  function apply() {
    if (targetCents == null) return 0;
    const text = formatMoney(targetCents);
    const els = amountEls();
    els.forEach((el) => { if (el.textContent !== text) el.textContent = text; });
    return els.length;
  }

  // Defend each open shadow root: the app re-renders the widget back to the
  // catalog price; re-apply our amount whenever it does. Attached once per root.
  function observeShadows() {
    document.querySelectorAll(PLACEMENTS).forEach((host) => {
      const root = host.shadowRoot;
      if (!root || root.__saltAmountObserved) return;
      root.__saltAmountObserved = true;
      new MutationObserver(() => apply())
        .observe(root, { childList: true, subtree: true, characterData: true });
    });
  }

  function refresh() { observeShadows(); return apply(); }

  function setTotal(totalCents) {
    if (typeof totalCents !== 'number' || isNaN(totalCents)) return;
    targetCents = Math.round(totalCents / INSTALLMENTS);
    refresh();
  }

  // 1) React to matrix selection changes (qty tier + one-time/subscription).
  document.addEventListener('purchasematrix:change', (e) => {
    if (e.detail) setTotal(e.detail.sale);
  });

  // 2) Seed the initial amount from the matrix's cached state (it dispatches
  //    once during connectedCallback, possibly before this listener attached).
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('purchase-matrix').then(() => {
      requestAnimationFrame(() => {
        const m = document.querySelector('purchase-matrix');
        if (m && m.lastChange) setTotal(m.lastChange.sale);
      });
    });
  }

  // 3) Bootstrap — the <square-placement> shadow content renders asynchronously
  //    after the app's script loads, so poll briefly (~10s) to attach observers
  //    and apply once it exists; then the shadow observers + the matrix-change
  //    listener keep it in sync.
  let tries = 0;
  const timer = setInterval(() => {
    refresh();
    if (++tries > 40) clearInterval(timer);
  }, 250);
})();
